import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { useToast } from '@aimsos/ui';

import { setDriveExtraction } from '../../../services/entityDrive.js';

/**
 * Turn claim extraction on or off for a folder of the drive (CT-5717). The
 * switch moves at once (`onOptimistic`); a failure moves it back (`onRollback`)
 * and says so. Turning it off says what that means, as the prototype does.
 */
export function useSetDriveExtraction({ entityType, entityId, onOptimistic, onRollback } = {}) {
  const { t } = useTranslation('unified-profiles');
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const submittingRef = useRef(false);

  const set = useCallback(
    async ({ folderId, enabled, name }) => {
      if (submittingRef.current) return false;
      submittingRef.current = true;
      setSaving(true);
      onOptimistic?.(enabled);
      try {
        await setDriveExtraction({ entityType, entityId, folderId, enabled });
        if (enabled) {
          toast.success({ title: t('knowledge.drive.extraction.onTitle', { name }), message: t('knowledge.drive.extraction.onMessage') });
        } else {
          toast.success({ title: t('knowledge.drive.extraction.offTitle', { name }), message: t('knowledge.drive.extraction.offMessage') });
        }
        return true;
      } catch (err) {
        onRollback?.(!enabled);
        toast.error({ title: t('knowledge.drive.extraction.errorTitle'), message: err?.message ?? t('knowledge.drive.extraction.errorMessage') });
        return false;
      } finally {
        submittingRef.current = false;
        setSaving(false);
      }
    },
    [entityType, entityId, onOptimistic, onRollback, toast, t],
  );

  return useMemo(() => ({ set, saving }), [set, saving]);
}

export default useSetDriveExtraction;
